import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  AdminTenantOverviewSchema,
  SetTenantDeactivatedResultSchema,
  type AdminTenantOverviewRow,
} from '@/lib/schemas/adminConsole';

/**
 * Is the signed-in user Sylva staff?
 *
 * Deliberately not derived from user_tenants.role: staff hold `owner` rows on
 * customer tenants in order to support them, so the tenant role cannot tell
 * staff and salon owner apart. This only drives what the UI offers -- the
 * database enforces the same check inside the functions it guards.
 */
export function useIsPlatformAdmin() {
  const { data = false, isLoading } = useQuery({
    queryKey: ['is-platform-admin'],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc('is_platform_admin' as never);
      if (error) throw error;
      return data === true;
    },
    staleTime: 10 * 60 * 1000,
  });

  return { isPlatformAdmin: data, isLoading };
}

export function useAdminTenantOverview(enabled: boolean) {
  return useQuery({
    queryKey: ['admin-tenant-overview'],
    enabled,
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<AdminTenantOverviewRow[]> => {
      const { data, error } = await supabase.rpc('admin_tenant_overview' as never);
      if (error) throw error;
      return AdminTenantOverviewSchema.parse(data ?? []);
    },
  });
}

export function useSetTenantDeactivated() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      { tenantId, deactivated }: { tenantId: string; deactivated: boolean },
    ): Promise<string | null> => {
      const { data, error } = await supabase.rpc(
        'set_tenant_deactivated' as never,
        { p_tenant_id: tenantId, p_deactivated: deactivated } as never,
      );
      if (error) throw error;
      return SetTenantDeactivatedResultSchema.parse(data ?? null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenant-overview'] });
      // The signed-in user may be an owner on the tenant just switched off, so
      // their own tenant list can change too.
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}
